import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  IconButton,
  TableSortLabel,
  Skeleton,
  TextField,
  Grid
} from '@mui/material'
import { Search as SearchIcon, Launch as LaunchIcon } from '@mui/icons-material'
import {
  healthScoreApi,
  FranchiseHealthScore,
  HealthScoresResponse
} from '../services/healthScoreApi'
import { isRequestCanceled } from '../services/core/errorHandler'
import { HealthScoreBadge } from './HealthScoreBadge'
import HealthScoreTooltip from './HealthScoreTooltip'
import HealthScoreTrends from './health-scores/HealthScoreTrends'
import { getMarketFilterOptions } from '../constants/markets'
import { poolwerxColors } from '../theme/colors'


type SortField = 'franchise_name' | 'country' | 'overall_score' | 'last_sync'
type SortOrder = 'asc' | 'desc'

// Helper function to calculate days ago
const getDaysAgo = (dateString?: string): number | null => {
  if (!dateString) return null
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - date.getTime())
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

// Helper function to format last sync display
const formatLastSync = (dateString?: string): string => {
  if (!dateString) return 'Never'
  const daysAgo = getDaysAgo(dateString)
  if (daysAgo === null) return 'Never'
  if (daysAgo === 0) return 'Today'
  if (daysAgo === 1) return '1 day ago'
  return `${daysAgo} days ago`
}

interface AdminHealthScoreTableProps {
  initialMarketFilter?: string
  initialRegionFilter?: string
}

export const AdminHealthScoreTable: React.FC<AdminHealthScoreTableProps> = ({
  initialMarketFilter = '',
  initialRegionFilter = ''
}) => {
  const navigate = useNavigate()
  const [data, setData] = useState<HealthScoresResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [marketFilter, setMarketFilter] = useState<string>(initialMarketFilter)
  const [regionFilter, setRegionFilter] = useState<string>(initialRegionFilter)
  const [availableRegions, setAvailableRegions] = useState<{region_code: string, region_name: string, market: string}[]>([])
  const [sortField, setSortField] = useState<SortField>('overall_score')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  
  // Request cancellation
  const abortControllerRef = useRef<AbortController | null>(null)
  
  // Debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  // Get market filter options from central constants
  const marketOptions = getMarketFilterOptions()
  
  // Fetch available regions from API
  const fetchRegions = async (market?: string) => {
    try {
      const response = await healthScoreApi.getRegions(market)
      setAvailableRegions(response.regions)
    } catch (err) {
      console.error('Failed to fetch regions:', err)
      setAvailableRegions([])
    }
  }
  
  // Get region options based on available regions
  const getRegionOptions = () => {
    if (!marketFilter) {
      return [{ value: '', label: 'Select Market First' }]
    }
    
    const filteredRegions = availableRegions.filter(region => 
      !marketFilter || region.market === marketFilter
    )
    
    return [
      { value: '', label: 'All Regions' },
      ...filteredRegions.map(region => ({
        value: region.region_code,
        label: region.region_name
      }))
    ]
  }

  const fetchHealthScores = async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    
    setLoading(true)
    setError(null)
    try {
      const response = await healthScoreApi.getHealthScores(
        marketFilter || undefined,
        regionFilter || undefined
      )
      
      // Only update state if request wasn't cancelled
      if (!abortController.signal.aborted) {
        setData(response)
      }
    } catch (err: any) {
      // Ignore cancellation errors
      if (!isRequestCanceled(err)) {
        setError(err.response?.data?.detail || 'Failed to fetch health scores')
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false)
      }
    }
  }
  
  // Debounced version of fetchHealthScores
  const fetchHealthScoresDebounced = () => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      fetchHealthScores()
    }, 300) // 300ms delay
  }

  // Handle initial load
  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch regions first
      await fetchRegions()
      // Then fetch health scores
      await fetchHealthScores()
      setIsInitialLoad(false)
    }
    loadInitialData()
    
    // Cleanup: cancel any in-flight requests on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])
  
  // Handle market filter changes
  useEffect(() => {
    if (!isInitialLoad && marketFilter !== undefined) {
      // Reset region filter when market changes
      setRegionFilter('')
      // Fetch regions for the new market
      fetchRegions(marketFilter)
      // Fetch health scores with new market filter (debounced)
      fetchHealthScoresDebounced()
    }
  }, [marketFilter])
  
  // Handle region filter changes
  useEffect(() => {
    if (!isInitialLoad && regionFilter !== undefined) {
      // Fetch health scores with debouncing
      fetchHealthScoresDebounced()
    }
  }, [regionFilter])

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const handleFranchiseClick = (fpCode: string) => {
    navigate(`/admin/health-scores/${fpCode}`)
  }

  const filteredAndSortedFranchises = React.useMemo(() => {
    if (!data) return []
    
    // Filter by search term
    let filtered = data.franchises.filter(franchise => {
      const searchMatch = !searchFilter || 
        franchise.franchise_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        franchise.fp_code.toLowerCase().includes(searchFilter.toLowerCase())
      return searchMatch
    })
    
    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any
      
      switch (sortField) {
        case 'franchise_name':
          aValue = a.franchise_name.toLowerCase()
          bValue = b.franchise_name.toLowerCase()
          break
        case 'country':
          aValue = a.country
          bValue = b.country
          break
        case 'overall_score':
          aValue = a.overall_health.score ?? -1
          bValue = b.overall_health.score ?? -1
          break
        case 'last_sync':
          aValue = getDaysAgo(a.last_sync_date) ?? 99999
          bValue = getDaysAgo(b.last_sync_date) ?? 99999
          break
        default:
          return 0
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
    
    return filtered
  }, [data, sortField, sortOrder, searchFilter])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading health scores...
          </Typography>
        </Stack>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    )
  }

  if (!data) return null

  return (
    <Box>
      {/* Search and Filter Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={9}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🔍 Search & Filter
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search by name/code"
                    variant="outlined"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="market-label">Market</InputLabel>
                    <Select
                      labelId="market-label"
                      id="market-select"
                      value={marketFilter}
                      onChange={(e) => setMarketFilter(e.target.value)}
                      label="Market"
                    >
                      {marketOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="region-label">Region</InputLabel>
                    <Select
                      labelId="region-label"
                      id="region-select"
                      value={regionFilter}
                      onChange={(e) => setRegionFilter(e.target.value)}
                      label="Region"
                      disabled={!marketFilter}
                    >
                      {getRegionOptions().map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          {/* Health Score Summary */}
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Health Score Summary</Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Good</Typography>
                  <Typography variant="h6" color="#4caf50">{data.summary.by_band.Good}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Watch</Typography>
                  <Typography variant="h6" color="#ff9800">{data.summary.by_band.Watch}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Poor</Typography>
                  <Typography variant="h6" color="#f44336">{data.summary.by_band.Poor}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Critical</Typography>
                  <Typography variant="h6" color="#212121">{data.summary.by_band.Critical}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No Data</Typography>
                  <Typography variant="h6" color="#9e9e9e">{data.summary.by_band['No Data']}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Health Scores Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              🏢 Franchise Partners
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredAndSortedFranchises.length} of {data.franchises.length} franchise partners
            </Typography>
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'franchise_name'}
                      direction={sortField === 'franchise_name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('franchise_name')}
                    >
                      <strong>Franchise</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'country'}
                      direction={sortField === 'country' ? sortOrder : 'asc'}
                      onClick={() => handleSort('country')}
                    >
                      <strong>Country</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell><strong>Region</strong></TableCell>
                  <TableCell><strong>State</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'overall_score'}
                      direction={sortField === 'overall_score' ? sortOrder : 'asc'}
                      onClick={() => handleSort('overall_score')}
                    >
                      <strong>Overall Health</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center"><strong>📦 Products</strong></TableCell>
                  <TableCell align="center"><strong>🧾 Sales</strong></TableCell>
                  <TableCell align="center"><strong>🔧 System</strong></TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'last_sync'}
                      direction={sortField === 'last_sync' ? sortOrder : 'asc'}
                      onClick={() => handleSort('last_sync')}
                    >
                      <strong>Last Sync</strong>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedFranchises.map((franchise) => {
              const hasNoSync = !franchise.last_sync_date
              return (
              <TableRow 
                key={franchise.fp_code}
                sx={hasNoSync ? { 
                  backgroundColor: 'rgba(158, 158, 158, 0.1)',
                  '& .MuiTableCell-root': {
                    color: 'text.secondary'
                  }
                } : {}}
              >
                <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleFranchiseClick(franchise.fp_code)}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {franchise.franchise_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {franchise.fp_code}
                      </Typography>
                      {franchise.system_connectivity && (
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                          {franchise.system_connectivity.lightspeed && (
                            <Chip label="LS" size="small" sx={{ height: 16, fontSize: 10 }} color="success" />
                          )}
                          {franchise.system_connectivity.xero && (
                            <Chip label="Xero" size="small" sx={{ height: 16, fontSize: 10 }} color="info" />
                          )}
                          {franchise.system_connectivity.field_management && (
                            <Chip label={franchise.country === 'US' ? 'Skimmer' : 'PT'} size="small" sx={{ height: 16, fontSize: 10 }} color="warning" />
                          )}
                        </Box>
                      )}
                    </Box>
                    <IconButton size="small" sx={{ opacity: 0.7 }}>
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>{franchise.country}</TableCell>
                <TableCell>{franchise.region}</TableCell>
                <TableCell>{franchise.state}</TableCell>
                <TableCell>{franchise.fp_status}</TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <HealthScoreBadge
                      band={franchise.overall_health.band}
                      color={franchise.overall_health.color}
                      score={franchise.overall_health.score}
                      showIcon={true}
                    />
                    <HealthScoreTrends
                      fpCode={franchise.fp_code}
                      domain="overall"
                      size="small"
                      showSparkline={false}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    {franchise.product_score ? (
                      <HealthScoreTooltip fp_code={franchise.fp_code} domain="product">
                        <div>
                          <HealthScoreBadge
                            band={franchise.product_score.band}
                            color={franchise.product_score.color}
                            score={franchise.product_score.score}
                            size="small"
                          />
                        </div>
                      </HealthScoreTooltip>
                    ) : (
                      <HealthScoreBadge
                        band="No Data"
                        color="grey"
                        score={null}
                        size="small"
                      />
                    )}
                    <HealthScoreTrends
                      fpCode={franchise.fp_code}
                      domain="product"
                      size="small"
                      showSparkline={false}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    {franchise.sale_score ? (
                      <HealthScoreTooltip fp_code={franchise.fp_code} domain="sale">
                        <div>
                          <HealthScoreBadge
                            band={franchise.sale_score.band}
                            color={franchise.sale_score.color}
                            score={franchise.sale_score.score}
                            size="small"
                          />
                        </div>
                      </HealthScoreTooltip>
                    ) : (
                      <HealthScoreBadge
                        band="No Data"
                        color="grey"
                        score={null}
                        size="small"
                      />
                    )}
                    <HealthScoreTrends
                      fpCode={franchise.fp_code}
                      domain="sale"
                      size="small"
                      showSparkline={false}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    {franchise.system_score ? (
                      <HealthScoreTooltip fp_code={franchise.fp_code} domain="system">
                        <div>
                          <HealthScoreBadge
                            band={franchise.system_score.band}
                            color={franchise.system_score.color}
                            score={franchise.system_score.score}
                            size="small"
                          />
                        </div>
                      </HealthScoreTooltip>
                    ) : (
                      <HealthScoreBadge
                        band="No Data"
                        color="grey"
                        score={null}
                        size="small"
                      />
                    )}
                    <HealthScoreTrends
                      fpCode={franchise.fp_code}
                      domain="system"
                      size="small"
                      showSparkline={false}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography 
                    variant="body2" 
                    color={hasNoSync ? 'text.secondary' : 'text.primary'}
                    sx={{ fontStyle: hasNoSync ? 'italic' : 'normal' }}
                  >
                    {formatLastSync(franchise.last_sync_date)}
                  </Typography>
                </TableCell>
              </TableRow>
            )
            })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  )
}